import { IServerSideGetRowsRequest } from 'ag-grid-community';
import { ServerSideBlotterSearchRequest } from 'apiConnector/blotters';
import { useServerSideTranslators } from 'components/Blotters/Quotes/Grid/hooks-serverside/use-translators';
import { ExternalFilter } from 'components/Blotters/Quotes/Grid/model';
import { useTranslateFilters } from 'components/shared/Blotter/Grid/Hooks/useRequestTranslator';
import { useCallback } from 'react';
import { ActivityFilter, IBlotterColumnDefinition } from 'state/blotters/common/blotter.model';

type ActivityScope = ServerSideBlotterSearchRequest['visibilityContext']['activityScope'];

function mapActivityFilter(activity: ActivityFilter): ActivityScope {
  switch (activity) {
    case 'all':
      return 'Everything';
    case 'myTeam':
      return 'MyTeams';
    case 'mySelf':
      return 'OnlyMe';
    default:
      return undefined;
  }
}

type MemoizedFilters = Pick<ServerSideBlotterSearchRequest, 'filters'>;

export function getPageSize(gridParams: IServerSideGetRowsRequest): number {
  if (gridParams.startRow === undefined || gridParams.endRow === undefined) {
    return 25;
  }
  return gridParams.endRow - gridParams.startRow;
}

export function getPageIndex(gridParams: IServerSideGetRowsRequest): number {
  if (gridParams.startRow === undefined || gridParams.endRow === undefined) {
    return 0;
  }
  return Math.floor(gridParams.startRow / getPageSize(gridParams));
}

export function useTranslateRequest<TData>(columns: readonly IBlotterColumnDefinition<TData>[]) {
  const translators = useServerSideTranslators();
  const translateFilters = useTranslateFilters(translators, columns);

  return useCallback(
    (
      gridParams: IServerSideGetRowsRequest,
      request: ExternalFilter & MemoizedFilters,
    ): ServerSideBlotterSearchRequest => {
      const orders: ServerSideBlotterSearchRequest['pageContext']['orders'] = gridParams.sortModel.map(
        ({ colId: field, sort: direction }) => ({ field, direction }),
      );
      const gridFilters = Object.values(translateFilters(gridParams)).filter(x => x !== null);
      const { currency, startDate, endDate, platforms, product } = request;
      const externalFilter = { currency, startDate, endDate, platforms, product };

      const pageSize = getPageSize(gridParams);
      const pageIndex = getPageIndex(gridParams);

      return {
        externalFilter,
        filters: gridFilters,
        fullTextContext: (request.fullTextSearch?.length ?? 0) > 0 ? { query: request.fullTextSearch! } : undefined,
        pageContext: { orders: orders.length > 0 ? orders : [], page: pageIndex, size: pageSize },
        visibilityContext: {
          activityScope: mapActivityFilter(request.activityScope),
        },
      };
    },
    [translateFilters],
  );
}
